/**
 * Plan anlegen: reine Rechenhilfen fuer Namen, Zuschnitt-Rahmen und den
 * scharfen Export (docs/planverortung-vertrag.md, Paket P2).
 *
 * Kein DOM, kein Canvas — PdfCropper, PlanBildZuschnitt und PlanAnlegen
 * rechnen hierueber, die Tests pruefen die Zahlen ohne Browser.
 */

/**
 * Seitenverhaeltnis A4 quer (297 : 210 ≈ √2). Ein Plan in diesem Format fuellt
 * die A4-quer-Seite im Bericht genau aus.
 */
export const A4_QUER = Math.SQRT2;

/** Lange Kante des PDF-Exports: ≈ 340 dpi auf A3, ≈ 240 dpi auf A2. */
export const EXPORT_LANGE_KANTE_PX = 4000;
/** Harte Obergrenze je Kante (wie `MAX_KANTE_PX` im ImageCropper). */
export const EXPORT_DECKEL_PX = 4096;
/** Canvas-Flaeche: iOS Safari verweigert Canvas ueber ≈ 16,7 Mio. Pixel. */
export const MAX_CANVAS_FLAECHE_PX = 16_000_000;
/** Mehr als 600 dpi bringt keine Schaerfe mehr, nur Dateigroesse. */
export const MAX_EXPORT_SKALA = 600 / 72;

export interface Punkt {
    x: number;
    y: number;
}

export interface Rahmen {
    x: number;
    y: number;
    w: number;
    h: number;
}

export interface Flaeche {
    breite: number;
    hoehe: number;
}

const klemmen = (wert: number, min: number, max: number): number => Math.min(Math.max(wert, min), max);
const positiv = (wert: number): boolean => Number.isFinite(wert) && wert > 0;

/** Rueckfallname, wenn kein Name vergeben wurde: „Plan n+1“. */
export function planRueckfallName(anzahlVorhanden: number): string {
    const n = Number.isFinite(anzahlVorhanden) && anzahlVorhanden > 0 ? Math.floor(anzahlVorhanden) : 0;
    return `Plan ${n + 1}`;
}

/**
 * Name mit Seitenzusatz: bei mehrseitigen PDFs „Name – S. 2“, sonst der Name.
 * Leer bleibt leer (der Zuschneider sperrt dann „Übernehmen“).
 */
export function planNameMitSeite(basis: string, seite?: number, seitenGesamt?: number): string {
    const name = (basis ?? '').replace(/\s+/g, ' ').trim();
    if (!name) return '';
    if (seite && seite > 0 && seitenGesamt && seitenGesamt > 1) return `${name} – S. ${seite}`;
    return name;
}

/**
 * Planname aus dem Dateinamen: Pfad und Endung weg, Unterstriche und
 * Mehrfach-Leerzeichen geglaettet. Eine Endung braucht mindestens einen
 * Buchstaben — „Grundriss 1.5“ bleibt so stehen.
 */
export function planNameAusDatei(dateiname: string, seite?: number, seitenGesamt?: number): string {
    const ohnePfad = ((dateiname ?? '').split(/[\\/]/).pop() ?? '').trim();
    const ohneEndung = ohnePfad.replace(/\.(?=[a-z0-9]*[a-z])[a-z0-9]{1,5}$/i, '');
    const glatt = ohneEndung.replace(/_+/g, ' ');
    return planNameMitSeite(glatt, seite, seitenGesamt);
}

/**
 * Rahmen beim Ziehen von `start` nach `aktuell`. Mit `verhaeltnis`
 * (Breite/Hoehe, z. B. `A4_QUER`) behaelt der Rahmen dieses Format: die
 * staerker gezogene Richtung bestimmt die Groesse. Die Zugrichtung bleibt
 * erhalten (auch nach links/oben), und der Rahmen bleibt innerhalb von
 * `grenzen` — bei festem Verhaeltnis schrumpft er dann als Ganzes.
 * `verhaeltnis` null = freier Rahmen.
 */
export function rahmenMitVerhaeltnis(
    start: Punkt,
    aktuell: Punkt,
    verhaeltnis: number | null | undefined,
    grenzen: Flaeche,
): Rahmen {
    const B = Math.max(0, grenzen.breite || 0);
    const H = Math.max(0, grenzen.hoehe || 0);
    const sx = klemmen(start.x, 0, B);
    const sy = klemmen(start.y, 0, H);
    const ax = klemmen(aktuell.x, 0, B);
    const ay = klemmen(aktuell.y, 0, H);

    if (!verhaeltnis || !positiv(verhaeltnis)) {
        return { x: Math.min(sx, ax), y: Math.min(sy, ay), w: Math.abs(ax - sx), h: Math.abs(ay - sy) };
    }

    const rechts = ax >= sx;
    const unten = ay >= sy;
    const platzB = rechts ? B - sx : sx;
    const platzH = unten ? H - sy : sy;

    let w = Math.max(Math.abs(ax - sx), Math.abs(ay - sy) * verhaeltnis);
    let h = w / verhaeltnis;
    if (w > platzB) { w = platzB; h = w / verhaeltnis; }
    if (h > platzH) { h = platzH; w = h * verhaeltnis; }

    return { x: rechts ? sx : sx - w, y: unten ? sy : sy - h, w, h };
}

/**
 * Groesster mittiger Rahmen mit festem Verhaeltnis, in Prozent des Bilds
 * (fuer react-image-crop, Einheit '%').
 */
export function groessterRahmenProzent(breite: number, hoehe: number, verhaeltnis: number): Rahmen {
    if (!positiv(breite) || !positiv(hoehe) || !positiv(verhaeltnis)) return { x: 0, y: 0, w: 100, h: 100 };
    if (breite / hoehe > verhaeltnis) {
        const w = ((hoehe * verhaeltnis) / breite) * 100;
        return { x: (100 - w) / 2, y: 0, w, h: 100 };
    }
    const h = (breite / verhaeltnis / hoehe) * 100;
    return { x: 0, y: (100 - h) / 2, w: 100, h };
}

/**
 * Massstab (pdf.js `scale`) fuer den scharfen Export eines Bereichs der
 * Groesse `seitenBreite × seitenHoehe` (PDF-Einheiten bei Massstab 1): die
 * lange Kante wird ≈ `langeKante` px, nie ueber `deckel`, nie ueber 600 dpi
 * und nie ueber die Canvas-Flaeche, die iOS noch zeichnet.
 */
export function exportSkala(
    seitenBreite: number,
    seitenHoehe: number,
    langeKante: number = EXPORT_LANGE_KANTE_PX,
    deckel: number = EXPORT_DECKEL_PX,
): number {
    if (!positiv(seitenBreite) || !positiv(seitenHoehe)) return 1;
    const ziel = Math.min(positiv(langeKante) ? langeKante : EXPORT_LANGE_KANTE_PX, positiv(deckel) ? deckel : EXPORT_DECKEL_PX);
    let skala = Math.min(ziel / Math.max(seitenBreite, seitenHoehe), MAX_EXPORT_SKALA);
    if (seitenBreite * seitenHoehe * skala * skala > MAX_CANVAS_FLAECHE_PX) {
        skala = Math.sqrt(MAX_CANVAS_FLAECHE_PX / (seitenBreite * seitenHoehe));
    }
    return skala;
}

/**
 * Massstab, mit dem eine Seite ganz in den sichtbaren Platz passt (Start am
 * Handy: sonst ragt ein A3-Plan bei 100 % weit ueber den Bildschirm).
 * Auf zwei Nachkommastellen, zwischen `min` und `max`.
 */
export function einpassSkala(
    inhaltBreite: number,
    inhaltHoehe: number,
    platzBreite: number,
    platzHoehe: number,
    min = 0.25,
    max = 2,
): number {
    if (!positiv(inhaltBreite) || !positiv(inhaltHoehe) || !positiv(platzBreite) || !positiv(platzHoehe)) return 1;
    const skala = Math.min(platzBreite / inhaltBreite, platzHoehe / inhaltHoehe);
    return Math.floor(klemmen(skala, min, max) * 100) / 100;
}

/**
 * Masse fuer ein Arbeits-Canvas (z. B. Bild drehen): Seitenverhaeltnis bleibt,
 * hoechstens `maxKante` je Kante und `maxFlaeche` Pixel insgesamt.
 */
export function flaecheBegrenzen(
    breite: number,
    hoehe: number,
    maxFlaeche: number = MAX_CANVAS_FLAECHE_PX,
    maxKante = 8192,
): Flaeche {
    const b = Math.max(1, Math.round(positiv(breite) ? breite : 1));
    const h = Math.max(1, Math.round(positiv(hoehe) ? hoehe : 1));
    const faktor = Math.min(1, maxKante / Math.max(b, h), Math.sqrt(maxFlaeche / (b * h)));
    return { breite: Math.max(1, Math.floor(b * faktor)), hoehe: Math.max(1, Math.floor(h * faktor)) };
}

/**
 * Punkt bei 90°-Drehung im Uhrzeigersinn (pdf.js `rotation` +90):
 * `hoeheVorher` ist die Hoehe VOR der Drehung (wird die neue Breite).
 * Oben links → oben rechts, unten links → oben links.
 */
export function punktDrehen90(punkt: Punkt, hoeheVorher: number): Punkt {
    return { x: hoeheVorher - punkt.y, y: punkt.x };
}

/** Radierpfade mitdrehen (Koordinaten unskaliert, siehe PdfCropper). */
export function radierungenDrehen90(pfade: ReadonlyArray<ReadonlyArray<Punkt>>, hoeheVorher: number): Punkt[][] {
    return pfade.map(pfad => pfad.map(p => punktDrehen90(p, hoeheVorher)));
}

/** Drehung 0/90/180/270 im Uhrzeigersinn, zusaetzlich zur Eigendrehung der PDF-Seite. */
export type Drehung = 0 | 90 | 180 | 270;

export function naechsteDrehung(drehung: Drehung): Drehung {
    return ((drehung + 90) % 360) as Drehung;
}

/**
 * pdf.js-Drehung einer Seite: `getViewport({ rotation })` ERSETZT die
 * Eigendrehung der Seite (/Rotate) — daher addieren, sonst kippt ein
 * gescannter Plan mit /Rotate 90 beim Drehen zurueck.
 */
export function pdfDrehung(seitenDrehung: number, drehung: Drehung): number {
    const eigen = Number.isFinite(seitenDrehung) ? seitenDrehung : 0;
    return (((eigen + drehung) % 360) + 360) % 360;
}
